import React, { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import axios from "axios";
import { MessageCircle, X, Send, Phone, Search, ArrowLeft, CheckCircle, Clock, AlertCircle } from "lucide-react";

const STATUS_COLORS = {
  open: { bg: "bg-amber-100", text: "text-amber-700", label: "Open" },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", label: "In Progress" },
  resolved: { bg: "bg-green-100", text: "text-green-700", label: "Resolved" },
  closed: { bg: "bg-gray-100", text: "text-gray-500", label: "Closed" },
};

const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu, new, track, chat
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [trackId, setTrackId] = useState("");
  const [ticket, setTicket] = useState(null);
  const [trackError, setTrackError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [lastTicketId, setLastTicketId] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [ticket]);

  const handleSubmitTicket = async () => {
    if (!name.trim() || !message.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, {
        name: name.trim(),
        email: email.trim() || null,
        subject: subject.trim() || "Live Chat Message",
        message: message.trim(),
        category: "general"
      });
      setLastTicketId(res.data.ticket_id);
      setSubmitted(true);
      // Auto-load the ticket to show auto-reply
      try {
        const tRes = await axios.get(`${API}/support/tickets/${res.data.ticket_id}`);
        setTicket(tRes.data);
        setView("chat");
      } catch {}
    } catch {
      alert("Failed to send. Please try again.");
    } finally { setSending(false); }
  };

  const handleTrack = async () => {
    if (!trackId.trim()) return;
    setTrackError("");
    try {
      const res = await axios.get(`${API}/support/tickets/${trackId.trim()}`);
      setTicket(res.data);
      setView("chat");
    } catch {
      setTrackError("Ticket not found. Check the ID and try again.");
    }
  };

  const resetChat = () => {
    setView("menu");
    setTicket(null);
    setSubmitted(false);
    setMessage("");
    setSubject("");
    setTrackId("");
    setTrackError("");
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60]" data-testid="live-chat-widget">
      {/* Chat Window */}
      {open && (
        <div className="absolute bottom-16 right-0 w-[340px] max-h-[480px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid="chat-window">
          {/* Header */}
          <div className="bg-green-600 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {view !== "menu" && (
                <button onClick={resetChat} className="text-white/80 hover:text-white">
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <p className="text-white font-bold text-sm">Fixedraws Support</p>
                <p className="text-green-100 text-[11px]">We typically reply instantly</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Menu */}
          {view === "menu" && (
            <div className="p-4 space-y-3 flex-1 overflow-y-auto">
              <p className="text-sm text-gray-600">How can we help you today?</p>
              <button
                onClick={() => { setView("new"); setSubmitted(false); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-green-50 hover:bg-green-100 border border-green-200 transition-colors text-left"
                data-testid="chat-new-ticket"
              >
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                  <MessageCircle className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Send a Message</p>
                  <p className="text-xs text-gray-500">Open a new support ticket</p>
                </div>
              </button>
              <button
                onClick={() => setView("track")}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors text-left"
                data-testid="chat-track-ticket"
              >
                <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <Search className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Track Ticket</p>
                  <p className="text-xs text-gray-500">Check your ticket status</p>
                </div>
              </button>
              <a
                href={`https://wa.me/2348068833390?text=${encodeURIComponent("Hi Madam Precious, I'd like to know more about Fixedraws subscriptions.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 transition-colors text-left"
                data-testid="chat-whatsapp"
              >
                <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-white" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.13 1.6 5.93L0 24l6.35-1.66a11.87 11.87 0 0 0 5.7 1.45h.01c6.56 0 11.9-5.34 11.9-11.9a11.83 11.83 0 0 0-3.44-8.41ZM12.06 21.6h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.77.98 1.01-3.67-.23-.38a9.86 9.86 0 0 1-1.52-5.24c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 6.99 2.9a9.82 9.82 0 0 1 2.9 6.99c0 5.45-4.44 9.9-9.88 9.9Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.77-1.65-2.07-.17-.3-.02-.47.13-.62.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.57-.48-.5-.66-.5l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47 0 1.45 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.08 4.48.71.3 1.26.48 1.69.62.71.22 1.36.19 1.87.11.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">WhatsApp Madam Precious</p>
                  <p className="text-xs text-gray-500">Chat instantly · 08068833390</p>
                </div>
              </a>
              <a
                href="mailto:support@fixedraws.com"
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors text-left"
                data-testid="chat-email-contact"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Send className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Email Support</p>
                  <p className="text-xs text-gray-500">support@fixedraws.com</p>
                </div>
              </a>
            </div>
          )}

          {/* New Ticket Form */}
          {view === "new" && !submitted && (
            <div className="p-4 space-y-3 flex-1 overflow-y-auto">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" data-testid="chat-name" />
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email (for reply notification)" type="email" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" data-testid="chat-email" />
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject (optional)" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400" data-testid="chat-subject" />
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="How can we help you?" rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none" data-testid="chat-message" />
              <button
                onClick={handleSubmitTicket}
                disabled={sending || !name.trim() || !message.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chat-send"
              >
                {sending ? "Sending..." : <><Send className="h-4 w-4" /> Send Message</>}
              </button>
            </div>
          )}

          {/* Track Ticket */}
          {view === "track" && !ticket && (
            <div className="p-4 space-y-3 flex-1 overflow-y-auto">
              <p className="text-sm text-gray-600">Enter your ticket ID to check the status:</p>
              <input
                value={trackId}
                onChange={e => setTrackId(e.target.value)}
                placeholder="e.g. tkt_abc123"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="chat-track-input"
                onKeyDown={e => e.key === "Enter" && handleTrack()}
              />
              {trackError && (
                <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{trackError}</p>
              )}
              <button
                onClick={handleTrack}
                disabled={!trackId.trim()}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="chat-track-btn"
              >
                <Search className="h-4 w-4" /> Find Ticket
              </button>
            </div>
          )}

          {/* Chat View (ticket details + replies) */}
          {view === "chat" && ticket && (
            <div className="flex-1 overflow-y-auto">
              {/* Ticket info bar */}
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-mono">{ticket.ticket_id}</p>
                  <p className="text-sm font-medium text-gray-900 line-clamp-1">{ticket.subject}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(STATUS_COLORS[ticket.status] || STATUS_COLORS.open).bg} ${(STATUS_COLORS[ticket.status] || STATUS_COLORS.open).text}`}>
                  {(STATUS_COLORS[ticket.status] || STATUS_COLORS.open).label}
                </span>
              </div>

              <div className="p-4 space-y-3">
                {/* Original message */}
                <div className="flex justify-end">
                  <div className="bg-green-600 text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%]">
                    <p className="text-sm">{ticket.message}</p>
                    <p className="text-[10px] text-green-200 mt-1">{ticket.name} — {new Date(ticket.created_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* Replies */}
                {(ticket.replies || []).map((r, i) => (
                  <div key={i} className={`flex ${r.from === "admin" || r.from === "system" ? "justify-start" : "justify-end"}`}>
                    <div className={`rounded-2xl px-3 py-2 max-w-[85%] ${r.from === "admin" || r.from === "system" ? "bg-gray-100 text-gray-800 rounded-tl-sm" : "bg-green-600 text-white rounded-tr-sm"}`}>
                      <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                      <p className={`text-[10px] mt-1 ${r.from === "admin" || r.from === "system" ? "text-gray-400" : "text-green-200"}`}>
                        {r.from === "system" ? "Auto-reply" : r.from === "admin" ? "Support Team" : "You"} — {new Date(r.sent_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}

                {ticket.status === "resolved" && (
                  <div className="flex items-center gap-2 justify-center py-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-xs text-green-600 font-medium">This ticket has been resolved</span>
                  </div>
                )}
                {ticket.status === "open" && (
                  <div className="flex items-center gap-2 justify-center py-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-xs text-amber-600 font-medium">Waiting for support team reply</span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 ${open ? "bg-gray-700" : "bg-green-600 hover:bg-green-700"}`}
        data-testid="chat-fab"
        aria-label="Live Chat"
      >
        {open ? <X className="h-7 w-7 text-white" /> : <MessageCircle className="h-7 w-7 text-white" />}
      </button>
      {!open && (
        <span className="absolute -top-1 -left-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse" />
      )}
    </div>
  );
};

export default LiveChat;
